"use client"

import { useCallback, useEffect, useState } from "react"
import { FieldArray, Formik, Form, useFormikContext } from "formik"
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2 } from "lucide-react"
import { useAuth } from "@/hooks/auth/use-auth"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  CHANNEL_LABELS,
  FOLLOW_UP_DEFAULTS,
  resolveFollowUpChannels,
  resolveSessionTimeoutMin,
  validateFollowUpsConfig,
  type ChannelType,
  type FollowUpStep,
  type FollowUpsConfig,
  type MessageAgentConfig,
} from "@/lib/agents/messaging/types"

import { CHANNEL_MESSAGING_WINDOW_HOURS } from "@/lib/agents/messaging/channels/channel-messaging-windows"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





type Unit = "minutes" | "hours" | "days"
const UNIT_MINUTES: Record<Unit, number> = { minutes: 1, hours: 60, days: 1440 }

function formatMinutes(minutes: number): string {
  const { amount, unit } = splitDelay(minutes)
  return `${amount} ${amount === 1 ? unit.slice(0, -1) : unit}`
}

/** Largest unit that divides the value cleanly, so 1200 shows as 20 hours. */
function splitDelay(minutes: number): { amount: number; unit: Unit } {
  if (minutes % UNIT_MINUTES.days === 0) return { amount: minutes / UNIT_MINUTES.days, unit: "days" }
  if (minutes % UNIT_MINUTES.hours === 0) return { amount: minutes / UNIT_MINUTES.hours, unit: "hours" }
  return { amount: minutes, unit: "minutes" }
}

interface StepFormValue {
  amount: number
  unit: Unit
  mode: FollowUpStep["mode"]
  text: string
}

interface FormValues {
  enabled: boolean
  steps: StepFormValue[]
  channels: ChannelType[]
}

function toFormValues(config: FollowUpsConfig | undefined): FormValues {
  const steps = config?.steps?.length ? config.steps : FOLLOW_UP_DEFAULTS.steps
  return {
    enabled: config?.enabled ?? false,
    channels: resolveFollowUpChannels(config),
    steps: steps.map((s) => ({
      ...splitDelay(s.delay_minutes),
      mode: s.mode,
      text: s.text ?? "",
    })),
  }
}

function toConfig(values: FormValues): FollowUpsConfig {
  return {
    enabled: values.enabled,
    channels: values.channels,
    steps: values.steps.map((s) => ({
      delay_minutes: Math.round(s.amount * UNIT_MINUTES[s.unit]),
      mode: s.mode,
      ...(s.mode === "fixed" ? { text: s.text.trim() } : {}),
    })),
  }
}

/** Client-side mirror of the server validator, which stays the authority. */
function validate(values: FormValues): Record<string, string> {
  const error = validateFollowUpsConfig(toConfig(values))
  return error ? { form: error } : {}
}

/** Only what the 24h-window warning needs, so this does not inherit the
    agent-channel vs channel-row type drift. */
type ChannelSummary = { channel_type: string; status: string }

interface FollowUpSettingsProps {
  agentId: string
  canManage: boolean
  channels: ChannelSummary[]
}

export function FollowUpSettings({ agentId, canManage, channels }: FollowUpSettingsProps) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsFollowUpSettings")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [config, setConfig] = useState<MessageAgentConfig | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const connectedTypes = [
    ...new Set(
      channels.filter((c) => c.status === "connected").map((c) => c.channel_type as ChannelType),
    ),
  ]

  const loadConfig = useCallback(async () => {
    if (!isAuthenticated) return
    setIsLoading(true)
    setLoadFailed(false)
    try {
      const response = await fetchWithAuth(`/api/agents/messaging/${agentId}`)
      if (!response.ok) throw new Error(`Config request failed: ${response.status}`)
      const data = await response.json()
      setConfig(data.agent?.config ?? {})
    } catch (err) {
      // The form must not fall back to defaults here: saving them would replace a
      // chain we never read and cancel its pending rows.
      console.error("Error loading agent config:", err)
      setLoadFailed(true)
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated, agentId])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  const save = useCallback(
    async (values: FormValues) => {
      setSaveError(null)
      setSaved(false)
      const follow_ups = toConfig(values)

      const response = await fetchWithAuth(`/api/agents/messaging/${agentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config: { follow_ups } }),
      })

      if (!response.ok) {
        const body = await response.json().catch(() => ({}))
        setSaveError(body.error ?? "Failed to save follow-ups")
        return
      }
      setConfig((prev) => (prev ? { ...prev, follow_ups } : prev))
      setSaved(true)
    },
    [fetchWithAuth, agentId],
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (loadFailed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle><span key={tUi("followUps")}>{tUi("followUps")}</span></CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertDescription>
              <span key={tUi("couldNotLoadThisAgentSFollowUp")}>{tUi("couldNotLoadThisAgentSFollowUp")}</span></AlertDescription>
          </Alert>
          <Button type="button" variant="outline" onClick={loadConfig}>
            <span key={tCommonUi("retry")}>{tCommonUi("retry")}</span></Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle><span key={tUi("followUps")}>{tUi("followUps")}</span></CardTitle>
        <CardDescription>
          <span key={tUi("whenACustomerGoesQuietAfterTheAgent")}>{tUi("whenACustomerGoesQuietAfterTheAgent")}</span></CardDescription>
      </CardHeader>

      <CardContent>
        <Formik
          initialValues={toFormValues(config?.follow_ups)}
          validate={validate}
          onSubmit={save}
          enableReinitialize
        >
          {({ values, isSubmitting, dirty }) => (
            <Form className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-[16rem]">
                  <Label htmlFor="follow-ups-enabled"><span key={tUi("enableFollowUps")}>{tUi("enableFollowUps")}</span></Label>
                  <p className="text-xs text-muted-foreground">
                    <span key={tUi("editingTheChainCancelsAnyFollowUpAlready")}>{tUi("editingTheChainCancelsAnyFollowUpAlready")}</span></p>
                </div>
                <EnabledSwitch disabled={!canManage} />
              </div>

              {values.enabled ? (
                <ChannelPicker canManage={canManage} connectedTypes={connectedTypes} />
              ) : null}

              {values.enabled ? (
                <StepList
                  canManage={canManage}
                  connectedTypes={connectedTypes}
                  sessionTimeoutMin={resolveSessionTimeoutMin(config)}
                />
              ) : null}

              <FormError />

              {saveError ? (
                <Alert variant="destructive">
                  <AlertDescription>{saveError}</AlertDescription>
                </Alert>
              ) : null}

              {canManage ? (
                <div className="flex items-center gap-3">
                  <Button type="submit" disabled={isSubmitting || !dirty}>
                    {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    <span><span key={tUi("save")}>{tUi("save")}</span></span>
                  </Button>
                  {saved && !dirty ? (
                    <span className="text-xs text-muted-foreground"><span key={tUi("saved")}>{tUi("saved")}</span></span>
                  ) : null}
                </div>
              ) : null}
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  )
}

function EnabledSwitch({ disabled }: { disabled: boolean }) {
  const { values, setFieldValue } = useFormikContext<FormValues>()
  return (
    <Switch
      id="follow-ups-enabled"
      checked={values.enabled}
      disabled={disabled}
      onCheckedChange={(checked) => void setFieldValue("enabled", checked)}
    />
  )
}

function FormError() {
  const { errors, submitCount } = useFormikContext<FormValues>()
  const error = (errors as { form?: string }).form
  if (!error || submitCount === 0) return null
  return (
    <Alert variant="destructive">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  )
}

/**
 * Which channels the chain runs on. Shows every connected channel plus any type the
 * config already selects — a selected-but-disconnected channel still counts towards
 * validation, so hiding it would let the operator save a chain scoped to channels
 * they cannot see.
 */
function ChannelPicker({
  canManage,
  connectedTypes,
}: {
  canManage: boolean
  connectedTypes: ChannelType[]
}) {
  const tUi = useTranslations("Messaging.AgentsMessagingSettingsFollowUpSettings")

  const { values, setFieldValue } = useFormikContext<FormValues>()
  const shown = [...new Set([...connectedTypes, ...values.channels])]

  const toggle = (type: ChannelType, checked: boolean) => {
    void setFieldValue(
      "channels",
      checked ? [...values.channels, type] : values.channels.filter((t) => t !== type),
    )
  }

  if (shown.length === 0) return null

  return (
    <div className="space-y-2">
      <Label><span key={tUi("channels")}>{tUi("channels")}</span></Label>
      <p className="text-xs text-muted-foreground">
        <span key={tUi("followUpsRunOnlyOnTheChannelsSelected")}>{tUi("followUpsRunOnlyOnTheChannelsSelected")}</span></p>
      <div className="flex flex-wrap gap-4">
        {shown.map((type) => (
          <label key={type} className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={values.channels.includes(type)}
              disabled={!canManage}
              onCheckedChange={(checked) => toggle(type, checked === true)}
              aria-label={CHANNEL_LABELS[type]}
            />
            {CHANNEL_LABELS[type]}
            {connectedTypes.includes(type) ? null : (
              <span className="text-xs text-muted-foreground"><span key={tUi("notConnected")}>{tUi("notConnected")}</span></span>
            )}
          </label>
        ))}
      </div>
    </div>
  )
}

interface BoundedChannel {
  label: string
  hours: number
}

/** The crossed windows for one step, each naming only the channels it actually blocks. */
function crossedWindows(
  boundedChannels: BoundedChannel[],
  cumulativeMinutes: number,
): Array<{ hours: number; labels: string[] }> {
  const byHours = new Map<number, string[]>()
  for (const channel of boundedChannels) {
    if (cumulativeMinutes < channel.hours * 60) continue
    byHours.set(channel.hours, [...(byHours.get(channel.hours) ?? []), channel.label])
  }
  return [...byHours.entries()]
    .sort(([a], [b]) => a - b)
    .map(([hours, labels]) => ({ hours, labels }))
}

function StepList({
  canManage,
  connectedTypes,
  sessionTimeoutMin,
}: {
  canManage: boolean
  connectedTypes: ChannelType[]
  sessionTimeoutMin: number
}) {


  const tUi = useTranslations("Messaging.AgentsMessagingSettingsFollowUpSettings")

  const { values, setFieldValue } = useFormikContext<FormValues>()
  // Connected AND selected: an unchecked channel is not part of the chain, and an
  // unconnected one cannot deliver anyway. Either way its window says nothing here.
  const boundedChannels = connectedTypes
    .filter((type) => values.channels.includes(type))
    .map((type) => ({ label: CHANNEL_LABELS[type], hours: CHANNEL_MESSAGING_WINDOW_HOURS[type] }))
    .filter((c): c is BoundedChannel => c.hours != null)

  return (
    <FieldArray name="steps">
      {({ push, remove, swap }) => (
        <div className="space-y-4">
          {values.steps.map((step, index) => {
            // Cumulative, not per-step: delays chain from one step to the next,
            // while the platform window is measured from the customer's last
            // message. `>=` matches the runtime, which requires strictly less.
            const cumulativeMinutes = values.steps
              .slice(0, index + 1)
              .reduce((sum, s) => sum + Math.round(s.amount * UNIT_MINUTES[s.unit]), 0)
            // Two independent bounds — raising the session timeout buys a platform
            // window nothing, and the window says nothing about Telegram.
            const outsideSession =
              sessionTimeoutMin > 0 && cumulativeMinutes >= sessionTimeoutMin
            const crossed = crossedWindows(boundedChannels, cumulativeMinutes)

            return (
              <div key={index} className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium"><TranslationText>{tUi.rich("stepValue0", { value0: String(index + 1) })}</TranslationText></span>
                  {canManage ? (
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={index === 0}
                        onClick={() => swap(index, index - 1)}
                        aria-label={tUi("moveStepValue0Up", { value0: String(index + 1) })}
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={index === values.steps.length - 1}
                        onClick={() => swap(index, index + 1)}
                        aria-label={tUi("moveStepValue0Down", { value0: String(index + 1) })}
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      {values.steps.length > 1 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          aria-label={tUi("removeStepValue0", { value0: String(index + 1) })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor={`steps.${index}.amount`}><span key={tUi("wait")}>{tUi("wait")}</span></Label>
                    <Input
                      id={`steps.${index}.amount`}
                      type="number"
                      min={1}
                      className="w-24"
                      disabled={!canManage}
                      value={step.amount}
                      onChange={(e) =>
                        void setFieldValue(`steps.${index}.amount`, Number(e.target.value))
                      }
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`steps.${index}.unit`}><span key={tUi("unit")}>{tUi("unit")}</span></Label>
                    <Select
                      value={step.unit}
                      disabled={!canManage}
                      onValueChange={(v) => void setFieldValue(`steps.${index}.unit`, v)}
                    >
                      <SelectTrigger id={`steps.${index}.unit`} className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="minutes"><span key={tUi("minutes")}>{tUi("minutes")}</span></SelectItem>
                        <SelectItem value="hours"><span key={tUi("hours")}>{tUi("hours")}</span></SelectItem>
                        <SelectItem value="days"><span key={tUi("days")}>{tUi("days")}</span></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`steps.${index}.mode`}><span key={tUi("message")}>{tUi("message")}</span></Label>
                    <Select
                      value={step.mode}
                      disabled={!canManage}
                      onValueChange={(v) => void setFieldValue(`steps.${index}.mode`, v)}
                    >
                      <SelectTrigger id={`steps.${index}.mode`} className="w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ai"><span key={tUi("aISmart")}>{tUi("aISmart")}</span></SelectItem>
                        <SelectItem value="fixed"><span key={tUi("fixedText")}>{tUi("fixedText")}</span></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {step.mode === "fixed" ? (
                  <div className="space-y-1.5">
                    <Label htmlFor={`steps.${index}.text`}><span key={tUi("text")}>{tUi("text")}</span></Label>
                    <Textarea
                      id={`steps.${index}.text`}
                      rows={2}
                      maxLength={FOLLOW_UP_DEFAULTS.MAX_TEXT_LENGTH}
                      disabled={!canManage}
                      value={step.text}
                      onChange={(e) => void setFieldValue(`steps.${index}.text`, e.target.value)}
                    />
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    <span key={tUi("theAgentDecidesWhetherANudgeIsAppropriate")}>{tUi("theAgentDecidesWhetherANudgeIsAppropriate")}</span></p>
                )}

                {outsideSession ? (
                  <Alert variant="destructive">
                    <AlertDescription className="text-xs">
                      {<span key={tUi("thisStepLandsMoreThanValue0AfterThe", { value0: String(formatMinutes(sessionTimeoutMin)) })}>{tUi("thisStepLandsMoreThanValue0AfterThe", { value0: String(formatMinutes(sessionTimeoutMin)) })}</span>}
                    </AlertDescription>
                  </Alert>
                ) : null}

                {crossed.map(({ hours, labels }) => (
                  <Alert key={hours}>
                    <AlertDescription className="text-xs">
                      {<span key={tUi("value0Value1MessagesWithinValue2HoursOfThe", { value0: String(labels.join(", ")), value1: String(labels.length === 1 ? "only allows" : "only allow"), value2: String(hours) })}>{tUi("value0Value1MessagesWithinValue2HoursOfThe", { value0: String(labels.join(", ")), value1: String(labels.length === 1 ? "only allows" : "only allow"), value2: String(hours) })}</span>}
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            )
          })}

          {canManage && values.steps.length < FOLLOW_UP_DEFAULTS.MAX_STEPS ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => push({ amount: 1, unit: "days", mode: "ai", text: "" })}
            >
              <Plus className="mr-2 h-4 w-4" />
              <span><span key={tUi("addStep")}>{tUi("addStep")}</span></span>
            </Button>
          ) : null}
        </div>
      )}
    </FieldArray>
  )
}
